import {
  AudienceType,
  Cadence,
  WorkflowResponseInput,
  WorkflowTriggerData,
} from '@/client/services/api/graphql/gql/graphql';

import AdminAssignmentsService from '@/client/components/admin/assignments/api/AdminAssignmentsService';
import type { AssignmentFormData } from '@/client/components/admin/assignments/types/assignmentTypes';
import { setSubmissionContentType } from '../utils/setSubmissionContentType';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export const useTriggerPointInTimeWorkflow = () => {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const navigate = useNavigate();
  const { watch } = useFormContext<AssignmentFormData>();

  const { id, settings, audience } = watch();

  const payload: WorkflowTriggerData = {
    workflowId: id,
    audience: audience.map((audienceItem) => ({
      dateRange: audienceItem.dateRange,
      days: audienceItem.days,
      id: audienceItem.id,
      matchingCriteria: audienceItem.matchingCriteria,
      type:
        audienceItem.type === AudienceType.PartnerType
          ? AudienceType.PartnerType
          : audienceItem.type === AudienceType.HiredDate
          ? AudienceType.HiredDate
          : audienceItem.segmentationType === 'group'
          ? AudienceType.Group
          : audienceItem.segmentationType === 'user'
          ? AudienceType.User
          : AudienceType.Segment,
    })),
    settingsOverrides: {
      daysToComplete: settings.daysToComplete ?? 0,
      assignmentOptions: settings.assignmentOptions,
      dueFromHired: settings.dueFromHired,
      escalateBuddy: settings.escalateBuddy,
      escalateManager: settings.escalateManager,
      escalationContacts: settings.escalationContacts.map((contact) => contact.id),
      name: settings.name,
      nonReminderMessages: settings.nonReminderMessages.map((message) => message.id),
      objectNumber: settings.objectNumber,
      objectType: settings.objectType,
      overdueMessages: settings.overdueMessages.map((message) => ({
        cadence: message.cadence as Cadence,
        message: message.message.id,
        totalMessages: message.totalMessages,
      })),
      partnerPermissions: settings.partnerPermissions,
      reminderMessages: settings.reminderMessages.map((reminder) => ({
        message: reminder.message.id,
        timeBeforeDue: reminder.timeBeforeDue,
      })),
      requireRecompletion: settings.requireRecompletion,
      responses: settings.responses.map((response) => ({
        ...response,
        content: {
          id: response?.content?.id,
          contentType: setSubmissionContentType(response?.content?.contentType),
        },
      })) as WorkflowResponseInput[],
      shareOptions: settings.shareOptions,
      triggerObject: settings.triggerObject?.id || null,
      type: settings.type,
    },
  };

  const triggerPointInTimeWorkflowMutation = useMutation({
    mutationFn: () => AdminAssignmentsService.triggerPointInTimeWorkflow(payload),
    onSuccess: () => {
      setToast({
        show: true,
        status: 'success',
        title: t('admin.assignments.instances.create.success'),
      });

      navigate(`/admin/assignments/details/${id}`);
    },
    onError: () => {
      setToast({
        show: true,
        status: 'error',
        title: t('admin.assignments.instances.create.error'),
      });
    },
  });

  return {
    triggerPointInTimeWorkflowMutation,
  };
};
